import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useAuth } from './use-auth';
import {
  getHealthNudges,
  dismissNudge,
  snoozeNudge,
} from '../services/health-intelligence-service';
import type { HealthNudge } from '../types/health-intelligence-types';

export const nudgeKeys = {
  all: ['healthNudges'] as const,
  list: (userId: string | undefined) => [...nudgeKeys.all, userId] as const,
};

/**
 * Hook to manage health nudges with optimistic updates
 *
 * @returns Query result with nudges and mutation methods
 */
export const useHealthNudges = () => {
  const { user } = useAuth();
  const userId = user?.id;
  const queryClient = useQueryClient();

  const queryResult = useQuery<HealthNudge[]>({
    queryKey: nudgeKeys.list(userId),
    queryFn: () => getHealthNudges(userId!),
    enabled: !!userId,
    staleTime: 5 * 60 * 1000, // 5 minutes
  });

  const nudges = queryResult.data ?? [];
  const unreadCount = nudges.filter((n) => !n.isRead).length;

  const dismissMutation = useMutation({
    mutationFn: (nudgeId: string) => dismissNudge(nudgeId),
    onMutate: async (nudgeId) => {
      // Cancel any outgoing refetches
      await queryClient.cancelQueries({ queryKey: nudgeKeys.list(userId) });

      // Snapshot the previous value
      const previousNudges = queryClient.getQueryData<HealthNudge[]>(
        nudgeKeys.list(userId)
      );

      // Optimistically update to the new value
      queryClient.setQueryData<HealthNudge[]>(
        nudgeKeys.list(userId),
        (old) => old?.filter((nudge) => nudge.id !== nudgeId) ?? []
      );

      // Return context with the snapshot
      return { previousNudges };
    },
    onError: (_err, _nudgeId, context) => {
      // Rollback to the previous value on error
      if (context?.previousNudges) {
        queryClient.setQueryData(nudgeKeys.list(userId), context.previousNudges);
      }
    },
    onSettled: () => {
      // Invalidate queries after mutation completes
      queryClient.invalidateQueries({ queryKey: nudgeKeys.list(userId) });
    },
  });

  const snoozeMutation = useMutation({
    mutationFn: ({ nudgeId, hours }: { nudgeId: string; hours: number }) =>
      snoozeNudge(nudgeId, hours),
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: nudgeKeys.list(userId) });
    },
  });

  return {
    ...queryResult,
    nudges,
    unreadCount,
    dismissNudge: dismissMutation.mutate,
    snoozeNudge: snoozeMutation.mutate,
  };
};
